import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { CircularProgress, Stack, Typography } from '@mui/material';
import AgricultureOutlinedIcon from '@mui/icons-material/AgricultureOutlined';
import { FormattedMessage } from 'react-intl';
import type { MultiPolygon, Polygon } from 'geojson';

import { GET_LPIS_DETAIL_QUERY, LpisDetail } from '../../../gql';
import { fetchLpisDeclaredCrops } from '../../../io/lpis';
import { SelectedEntityCloseButton } from '../SelectedEntityCloseButton';

const headingStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.75,
  fontWeight: 500,
  color: 'text.primary',
  lineHeight: 1.25,
};
const iconStyles = { fontSize: 16, flexShrink: 0 };
const secondaryTextStyles = {
  color: 'text.secondary',
  fontSize: 12,
  lineHeight: 1.25,
};
const bulletLineStyles = {
  ...secondaryTextStyles,
  '& > span + span::before': {
    content: '"•"',
    mx: 0.5,
  },
};

const cropLoadingStyles = {
  ...secondaryTextStyles,
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
};
const cropSpinnerProps = { size: 10, thickness: 5, color: 'inherit' as const };

const renderSeparatedValue = (value: string, index: number) => (
  <span key={index}>{value}</span>
);

function formatUserName(detail: LpisDetail | null, fallback?: string | null) {
  const person = [detail?.firstName, detail?.lastName]
    .filter(Boolean)
    .join(' ');
  const names = [detail?.companyName, person || null].filter(Boolean);

  if (names.length > 0) {
    return names.join(' · ');
  }

  return fallback || null;
}

function formatAreaHa(areaHa: number | null) {
  if (areaHa === null) {
    return null;
  }

  return `${areaHa.toLocaleString('cs-CZ', { maximumFractionDigits: 2 })} ha`;
}

export const LpisEntity = React.memo(
  ({
    parcelIds,
    code,
    userName,
    userId,
    areaHa,
    geometry,
  }: {
    parcelIds: number[];
    code: string;
    userName?: string | null;
    userId?: number | null;
    areaHa?: number | null;
    geometry?: Polygon | MultiPolygon | null;
  }) => {
    const { data, loading, error } = useQuery<
      { getLpisDetail: LpisDetail | null },
      { parcelIds: number[]; userId?: number | null; code?: string | null }
    >(GET_LPIS_DETAIL_QUERY, {
      variables: { parcelIds, userId: userId ?? null, code },
    });
    const [declaredCrop, setDeclaredCrop] = useState<string | null>(null);
    const [cropLoading, setCropLoading] = useState(false);
    const detail = data?.getLpisDetail;
    const title = detail?.code || code;
    const resolvedUser = formatUserName(detail, userName);
    const location = [detail?.square, detail?.nationalCode]
      .filter(Boolean)
      .join(' · ');
    const metadata = [
      detail?.culture,
      formatAreaHa(detail?.areaHa ?? areaHa ?? null),
    ].filter((value): value is string => Boolean(value));
    const contacts = [detail?.phone, detail?.email].filter(
      (value): value is string => Boolean(value)
    );
    const cropValues = { crop: declaredCrop };
    const showDetailError = !loading && Boolean(error);

    useEffect(() => {
      if (!geometry) {
        setDeclaredCrop(null);
        setCropLoading(false);
        return;
      }

      const controller = new AbortController();

      setDeclaredCrop(null);
      setCropLoading(true);

      const loadDeclaredCrop = async () => {
        try {
          const cropName = await fetchLpisDeclaredCrops(
            geometry,
            controller.signal,
            { code, parcelId: parcelIds[0] }
          );

          if (!controller.signal.aborted) {
            setDeclaredCrop(cropName);
            setCropLoading(false);
          }
        } catch {
          if (!controller.signal.aborted) {
            setDeclaredCrop(null);
            setCropLoading(false);
          }
        }
      };

      void loadDeclaredCrop();

      return () => {
        controller.abort();
      };
    }, [geometry, code, parcelIds]);

    return (
      <Stack
        my={1}
        pr={4}
        gap={0.25}
        position="relative"
        data-selected-entity-card
      >
        <SelectedEntityCloseButton />
        <Typography component="div" sx={headingStyles}>
          <AgricultureOutlinedIcon titleAccess="LPIS" sx={iconStyles} />
          {title}
        </Typography>
        {resolvedUser && (
          <Typography variant="body2" fontSize={13} lineHeight={1.25}>
            {resolvedUser}
          </Typography>
        )}
        {contacts.length > 0 && (
          <Typography sx={bulletLineStyles}>
            {contacts.map(renderSeparatedValue)}
          </Typography>
        )}
        {location && (
          <Typography sx={secondaryTextStyles}>{location}</Typography>
        )}
        {metadata.length > 0 && (
          <Typography sx={bulletLineStyles}>
            {metadata.map(renderSeparatedValue)}
          </Typography>
        )}
        {cropLoading && (
          <Typography sx={cropLoadingStyles} role="status">
            <CircularProgress {...cropSpinnerProps} />
            <FormattedMessage
              id="map.lpis.cropLoading"
              defaultMessage="Crop loading"
            />
          </Typography>
        )}
        {!cropLoading && declaredCrop && (
          <Typography sx={secondaryTextStyles}>
            <FormattedMessage
              id="map.lpis.crop"
              defaultMessage="Crop: {crop}"
              values={cropValues}
            />
          </Typography>
        )}
        {loading && (
          <Typography variant="body2" color="text.secondary" role="status">
            <FormattedMessage
              id="map.lpis.loading"
              defaultMessage="Loading LPIS…"
            />
          </Typography>
        )}
        {showDetailError && (
          <Typography variant="body2" color="text.secondary" role="status">
            <FormattedMessage
              id="map.lpis.detailError"
              defaultMessage="Details could not be loaded."
            />
          </Typography>
        )}
      </Stack>
    );
  }
);
